import { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  MenuItem,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Header from "../../../../components/Header";
import { AssetFinanceUrl } from "../../../../config";
import { executeDynamicSubmit, fetchJsonDynamic } from "../../../../data/DynamicServiceNew/jsondynamicservice";
import Swal from "sweetalert2";

const base_url = AssetFinanceUrl.uri;

const bodyTypeOptions = [
  "Station Wagon",
  "SUV",
  "Sedan",
  "Hatchback",
  "Pickup",
  "Van / Minibus",
  "Lorry / Truck",
  "Coupe",
  "Convertible",
  "Bus",
  "Other",
];

const fuelTypeOptions = ["Petrol", "Diesel", "Hybrid", "Electric", "CNG/LPG"];

const Form = (props) => {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const [carMakes, setCarMakes] = useState([]);
  const [loadingCarMakes, setLoadingCarMakes] = useState(true);
  const [makesEndpointAvailable, setMakesEndpointAvailable] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const response = await fetchJsonDynamic("allCarMakes", null, null, base_url);
        // If the field doesn't exist in the schema the response will have an error
        if (response?.error) {
          // endpoint not yet deployed — fall back to free text
          setMakesEndpointAvailable(false);
          setCarMakes([]);
        } else {
          const results = response?.results ? JSON.parse(response.results) : [];
          setCarMakes(results.filter((m) => m.IsActive !== false));
        }
      } catch (err) {
        console.error("Failed to fetch car makes:", err);
        setMakesEndpointAvailable(false);
      } finally {
        setLoadingCarMakes(false);
      }
    })();
  }, []);


  const initialValues = {
    code: props.data ? props.data.Code : "",
    make: props.data ? props.data.Make : "",
    makeId: props.data ? (props.data.MakeId || "") : "",
    modelName: props.data ? props.data.ModelName : "",
    bodyType: props.data ? props.data.BodyType : "",
    fuelType: props.data ? props.data.FuelType : "",
    indicativeBaseValue: props.data ? props.data.IndicativeBaseValue : "",
    ltvPct: props.data ? (props.data.LtvPct ?? props.data.ltvPct ?? "") : "",
    displayOrder: props.data ? props.data.DisplayOrder : "",
    isActive: props.data ? !!props.data.IsActive : true,
  };

  const validationSchema = yup.object().shape({
    code: yup.string().required("Code is required"),
    make: yup.string().required("Make is required (e.g. Toyota)"),
    modelName: yup.string().required("Model name is required (e.g. Probox)"),
    bodyType: yup.string().nullable(),
    fuelType: yup.string().nullable(),
    indicativeBaseValue: yup.number().typeError("Enter a valid number").nullable(),
    ltvPct: yup.number().typeError("Enter a valid percentage").min(0, "LTV cannot be negative").max(100, "LTV percentage cannot exceed 100%").nullable(),
    displayOrder: yup.number().typeError("Enter a valid number").nullable(),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    const carModel = {
      code: values.code,
      make: values.make,
      makeId: values.makeId ? parseInt(values.makeId, 10) : null,
      modelName: values.modelName,
      bodyType: values.bodyType || "",
      fuelType: values.fuelType || "",
      indicativeBaseValue: values.indicativeBaseValue ? parseFloat(values.indicativeBaseValue) : null,
      ltvPct: values.ltvPct !== "" && values.ltvPct != null ? parseFloat(values.ltvPct) : null,
      displayOrder: values.displayOrder ? parseInt(values.displayOrder, 10) : 0,
      companyId: parseInt(decodedToken.CompanyId, 10),
      isActive: values.isActive,
    };

    if (props.isEditing) {
      carModel.id = props.data.Id;
    }

    await executeDynamicSubmit({
      mutationName: props.isEditing ? "updateCarModel" : "createCarModel",
      variables: carModel,
      mutationObjectName: "carModel",
      baseUrl: base_url,
      isEditing: props.isEditing,
      entityName: "Car Model",
      onSuccess: () => {
        props.onClose();
        props.onAction();
      },
      setSubmitting,
    });
  };

  return (
    <Box m="20px">
      <Header
        title={props.isEditing ? "Edit Car Model" : "Create Car Model"}
        subtitle={
          props.isEditing
            ? "Edit an Existing Car Model"
            : "Create a New Car Model for Vehicle Collateral Setup"
        }
      />

      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box display="grid" gap="15px" gridTemplateColumns="repeat(4, minmax(0, 1fr))">
              <TextField
                fullWidth
                variant="filled"
                label="Code *"
                name="code"
                placeholder="e.g. TOY-PRB"
                value={values.code}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.code && !!errors.code}
                helperText={touched.code && errors.code}
                sx={{ gridColumn: "span 2" }}
              />

              {/* Make: dropdown if carmake table exists, text field as fallback */}
              {(!loadingCarMakes && (carMakes.length > 0)) ? (
                <TextField
                  fullWidth
                  select
                  variant="filled"
                  label="Make *"
                  name="make"
                  value={values.make}
                  onChange={(e) => {
                    const selectedName = e.target.value;
                    const matchedMake = carMakes.find(
                      (m) => (m.MakeName || m.Name || m.Make) === selectedName
                    );
                    setFieldValue("make", selectedName);
                    setFieldValue("makeId", matchedMake?.Id || matchedMake?.id || "");
                  }}
                  onBlur={handleBlur}
                  error={!!touched.make && !!errors.make}
                  helperText={touched.make && errors.make}
                  sx={{ gridColumn: "span 2" }}
                >
                  <MenuItem value="">-- Select Make --</MenuItem>
                  {carMakes.map((makeItem) => {
                    const makeName = makeItem.MakeName || makeItem.Name || makeItem.Make;
                    return (
                      <MenuItem key={makeItem.Id || makeItem.Code || makeName} value={makeName}>
                        {makeName} {makeItem.Code ? `(${makeItem.Code})` : ""}
                      </MenuItem>
                    );
                  })}
                </TextField>
              ) : (
                <TextField
                  fullWidth
                  variant="filled"
                  label={loadingCarMakes ? "Make * (Loading...)" : "Make *"}
                  name="make"
                  placeholder="e.g. Toyota"
                  value={values.make}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.make && !!errors.make}
                  helperText={touched.make && errors.make}
                  sx={{ gridColumn: "span 2" }}
                />
              )}

              <TextField
                fullWidth
                variant="filled"
                label="Model Name *"
                name="modelName"
                placeholder="e.g. Probox"
                value={values.modelName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.modelName && !!errors.modelName}
                helperText={touched.modelName && errors.modelName}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                select
                variant="filled"
                label="Asset Type"
                name="bodyType"
                value={values.bodyType}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.bodyType && !!errors.bodyType}
                helperText={touched.bodyType && errors.bodyType}
                sx={{ gridColumn: "span 2" }}
              >
                <MenuItem value="">-- Select Asset Type --</MenuItem>
                {bodyTypeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>


              <TextField
                fullWidth
                select
                variant="filled"
                label="Fuel Type"
                name="fuelType"
                value={values.fuelType}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.fuelType && !!errors.fuelType}
                helperText={touched.fuelType && errors.fuelType}
                sx={{ gridColumn: "span 2" }}
              >
                <MenuItem value="">-- Select Fuel Type --</MenuItem>
                {fuelTypeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                fullWidth
                variant="filled"
                type="number"
                label="Indicative Base Value (KES)"
                name="indicativeBaseValue"
                placeholder="e.g. 1200000"
                value={values.indicativeBaseValue}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.indicativeBaseValue && !!errors.indicativeBaseValue}
                helperText={touched.indicativeBaseValue && errors.indicativeBaseValue}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                variant="filled"
                type="number"
                label="LTV (%)"
                name="ltvPct"
                placeholder="e.g. 80"
                inputProps={{ min: 0, max: 100, step: "0.1" }}
                value={values.ltvPct}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.ltvPct && !!errors.ltvPct}
                helperText={(touched.ltvPct && errors.ltvPct) || "Max 100%"}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                variant="filled"
                type="number"
                label="Display Order"
                name="displayOrder"
                value={values.displayOrder}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.displayOrder && !!errors.displayOrder}
                helperText={touched.displayOrder && errors.displayOrder}
                sx={{ gridColumn: "span 2" }}
              />

              <Box sx={{ gridColumn: "span 4", display: "flex", alignItems: "center" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.isActive}
                      onChange={(e) => setFieldValue("isActive", e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Is Active"
                />
              </Box>
            </Box>

            <Box display="flex" justifyContent="end" mt="20px">
              <Button
                type="button"
                color="secondary"
                variant="outlined"
                onClick={props.onClose}
                sx={{ mr: 2 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={isSubmitting}
              >
                {props.isEditing ? "Update Car Model" : "Create Car Model"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default Form;
